/**
 * TrackBy utility functions for optimizing Angular *ngFor performance
 */

export class TrackByUtils {
  /**
   * Track by ID - most common use case
   */
  static trackById<T extends { id: string | number }>(index: number, item: T): string | number {
    return item?.id ?? index;
  }

  /**
   * Track by UUID
   */
  static trackByUuid<T extends { uuid: string }>(index: number, item: T): string | number {
    return item?.uuid ?? index;
  }

  /**
   * Track by code - useful for parameters and configuration items
   */
  static trackByCode<T extends { code: string }>(index: number, item: T): string | number {
    return item?.code ?? index;
  }

  /**
   * Track by name - when name is unique
   */
  static trackByName<T extends { name: string }>(index: number, item: T): string | number {
    return item?.name ?? index;
  }

  /**
   * Track by index - fallback when no unique identifier exists
   */
  static trackByIndex(index: number): number {
    return index;
  }

  /**
   * Track by multiple properties - for complex objects
   */
  static trackByComposite<T>(properties: (keyof T)[]): (index: number, item: T) => string {
    return (index: number, item: T): string => {
      if (!item) return index.toString();

      const values = properties.map(prop => item[prop]).filter(val => val != null);
      return values.length > 0 ? values.join('_') : index.toString();
    };
  }

  /**
   * Track by property - generic function for any property
   */
  static trackByProperty<T>(propertyName: keyof T): (index: number, item: T) => unknown {
    return (index: number, item: T): unknown => {
      return item?.[propertyName] ?? index;
    };
  }

  /**
   * Track by nested property - for objects with nested properties
   */
  static trackByNestedProperty<T>(path: string): (index: number, item: T) => unknown {
    return (index: number, item: T): unknown => {
      if (!item) return index;

      const value = path
        .split('.')
        .reduce((obj: unknown, key: string) => (obj as Record<string, unknown>)?.[key], item);
      return value ?? index;
    };
  }
}

/**
 * Common trackBy functions for specific use cases
 */
export const CommonTrackBy = {
  parameter: TrackByUtils.trackById,
  parameterDetail: TrackByUtils.trackById,
  user: TrackByUtils.trackById,
  role: TrackByUtils.trackByCode,
  menuItem: TrackByUtils.trackByName,
  tableRow: TrackByUtils.trackById,
  listItem: TrackByUtils.trackById
} as const;
